/**
 * The Cases Dashboard tab — GTI-Hub parity with the Backoffice
 * "GTI Project Ops Dashboard" sheet (All Cases / Completed tabs).
 *
 * One table row per case; a case with several NP assignments spans one
 * row per partner with the case-level cells left blank on continuation
 * rows (the sheet's "merged cell" effect). Analyst / view / search
 * filtering happens client-side over the single dashboard fetch.
 */

import { memo, ReactNode, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  ActionIcon,
  Badge,
  Button,
  Center,
  Group,
  Loader,
  Menu,
  Popover,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import {
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconPencil,
  IconRefresh,
  IconSearch,
  IconSelector,
} from '@tabler/icons-react';

import { useAnalystRoster, useTeams } from '@/hooks';
import { useSearchDraft } from '@/hooks/useSearchDraft';
import {
  CaseDashboardResponse,
  DashboardCase,
  DashboardNp,
  DashboardView,
} from '@/types/caseDashboard';
import { InboxAnalyst } from '@/types/inbox';
import { dueInstant, formatDueDate } from '@/utils';
import { AnalystAvatar, OwnerAvatarMenu } from './QueueRow';
import { timeAgo } from './timeAgo';

const UNASSIGNED = 'unassigned';
const DAY_MS = 24 * 3_600_000;
/** Blank case-level cells on NP continuation rows (the sheet's
 * "merged cell" effect). */
const PAD_KEYS = ['analyst', 'qa1', 'qa2', 'case', 'client', 'ref', 'opened', 'stage', 'due'];

/** The report's two human QA slots (ops "1st / 2nd QAer"). */
export type QaSlot = 'first' | 'second';

const QA_SLOT_LABELS: Record<QaSlot, string> = { first: '1st QA', second: '2nd QA' };

interface DashboardTabProps {
  data: CaseDashboardResponse | null;
  isLoading: boolean;
  error: Error | null;
  view: DashboardView;
  onViewChange: (next: DashboardView) => void;
  /** Analyst userId, `'unassigned'`, or null = everyone. URL-persisted
   * by the page so shared links keep the slice. */
  analystFilter: string | null;
  onAnalystFilterChange: (next: string | null) => void;
  /** Client team id, or null = every client. URL-persisted like the
   * analyst filter (`?client=`), so a per-client view can be shared. */
  clientFilter: string | null;
  onClientFilterChange: (next: string | null) => void;
  /** Committed (URL-persisted) search value; seeds the local draft —
   * filtering itself runs keystroke-instant on the draft. */
  search: string;
  /** Fired once per settled (debounced) draft — the URL write. */
  onSearchChange: (next: string) => void;
  onSaveNotes: (reportRequestId: string, notes: string | null) => Promise<void>;
  onRefresh: () => Promise<void>;
  /** For the avatar claim/assign menu — same semantics as the Queue:
   * claims write the case's analyst family-wide. */
  currentUserId: string | null;
  onClaim: (familyKey: string, userId: string | null) => Promise<void>;
  /** QA userId, `'unassigned'` (no QA at all), or null = everyone —
   * URL-persisted like the analyst filter; matches either QA slot. */
  qaFilter: string | null;
  onQaFilterChange: (next: string | null) => void;
  /** Writes ONE QA slot on the case's report (admin-only endpoint). */
  onAssignQa: (reportId: string, slot: QaSlot, userId: string | null) => Promise<void>;
}

// ---------------------------------------------------------------------------
// Presentation maps
// ---------------------------------------------------------------------------

const TRIAGE_STAGE_LABELS: Record<string, string> = {
  initial: 'Triage',
  subjects_extracted: 'Subjects extracted',
  subjects_identified: 'Subjects identified',
  plan_built: 'Plan built',
  np_selected: 'Partners selected',
  ready_to_start: 'Ready to start',
  started: 'Started',
};

const REPORT_STAGE_LABELS: Record<string, { label: string; color: string }> = {
  queued: { label: 'Queued', color: 'blue' },
  processing: { label: 'Processing', color: 'blue' },
  awaiting_profile_review: { label: 'Profile review', color: 'orange' },
  awaiting_match_review: { label: 'Match review', color: 'grape' },
  pending_activation: { label: 'Awaiting activation', color: 'teal' },
  awaiting_fulfilment: { label: 'With partners', color: 'blue' },
  awaiting_file_selection: { label: 'File selection', color: 'orange' },
  in_qa: { label: 'In QA', color: 'cyan' },
  processing_feedback: { label: 'Feedback', color: 'violet' },
  failed: { label: 'Failed', color: 'red' },
  completed: { label: 'Completed', color: 'green' },
};

function stageChip(c: DashboardCase): { label: string; color: string } {
  if (c.stageKind === 'report') {
    return REPORT_STAGE_LABELS[c.stage] ?? { label: c.stage.replace(/_/g, ' '), color: 'gray' };
  }
  return { label: TRIAGE_STAGE_LABELS[c.stage] ?? c.stage.replace(/_/g, ' '), color: 'gray' };
}

const NP_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  draft: { label: 'Draft', color: 'gray' },
  shortlisting: { label: 'Shortlisting', color: 'gray' },
  np_locked: { label: 'Locked in', color: 'blue' },
  in_progress: { label: 'In progress', color: 'blue' },
  submitted: { label: 'Submitted', color: 'green' },
};

function npStatusChip(status: string): { label: string; color: string } {
  return NP_STATUS_LABELS[status] ?? { label: status.replace(/_/g, ' '), color: 'gray' };
}

// ---------------------------------------------------------------------------
// Column sorting + due-date filter machinery (client-side, like search)
// ---------------------------------------------------------------------------

/** Pipeline rank for stage sorting: triage stages first (map order),
 * report stages after — sorting by rank reads as pipeline progress. */
const STAGE_ORDER: Record<string, number> = Object.fromEntries([
  ...Object.keys(TRIAGE_STAGE_LABELS).map((k, i) => [k, i] as const),
  ...Object.keys(REPORT_STAGE_LABELS).map((k, i) => [k, 100 + i] as const),
]);

const NP_STATUS_ORDER: Record<string, number> = {
  draft: 0,
  shortlisting: 1,
  np_locked: 2,
  in_progress: 3,
  submitted: 4,
};

export type DashboardSortKey =
  | 'analyst'
  | 'qa1'
  | 'qa2'
  | 'case'
  | 'client'
  | 'ref'
  | 'opened'
  | 'stage'
  | 'due'
  | 'completed'
  | 'delivery'
  | 'partner'
  | 'partnerStatus'
  | 'partnerDue'
  | 'notes';

export interface DashboardSort {
  key: DashboardSortKey;
  dir: 'asc' | 'desc';
}

/** Comparable per column; null = "no value", always sorted last. */
function sortValue(
  c: DashboardCase,
  key: DashboardSortKey,
  clientName: string | null
): string | number | null {
  switch (key) {
    case 'analyst':
      return c.analyst ? (c.analyst.name ?? c.analyst.userId).toLowerCase() : null;
    case 'qa1':
      return c.firstQa ? (c.firstQa.name ?? c.firstQa.userId).toLowerCase() : null;
    case 'qa2':
      return c.secondQa ? (c.secondQa.name ?? c.secondQa.userId).toLowerCase() : null;
    case 'case':
      return c.codename?.toLowerCase() ?? null;
    case 'client':
      return clientName?.toLowerCase() ?? null;
    case 'ref':
      return c.clientReference?.toLowerCase() ?? null;
    case 'opened':
      return c.createdAt ? Date.parse(c.createdAt) : null;
    case 'stage':
      return STAGE_ORDER[c.stage] ?? (c.stageKind === 'report' ? 150 : 50);
    case 'due':
      return c.dueAt ? Date.parse(c.dueAt) : null;
    case 'completed':
      return c.completedAt ? Date.parse(c.completedAt) : null;
    case 'delivery':
      return c.onTime === null ? null : c.onTime ? 0 : 1;
    case 'partner':
      return c.nps[0]?.partnerName?.toLowerCase() ?? null;
    case 'partnerStatus': {
      const ranks = c.nps.map((np) => NP_STATUS_ORDER[np.status] ?? -1);
      return ranks.length ? Math.max(...ranks) : null;
    }
    case 'partnerDue': {
      const dates = c.nps
        .map((np) => (np.npDueDate ? dueInstant(np.npDueDate) : null))
        .filter((d): d is number => d !== null);
      return dates.length ? Math.min(...dates) : null;
    }
    case 'notes':
      return c.analystNotes?.toLowerCase() ?? null;
  }
}

function compareCases(
  a: DashboardCase,
  b: DashboardCase,
  sort: DashboardSort,
  clientNameFor: (c: DashboardCase) => string | null
): number {
  const dir = sort.dir === 'asc' ? 1 : -1;
  const va = sortValue(a, sort.key, clientNameFor(a));
  const vb = sortValue(b, sort.key, clientNameFor(b));
  if (va === null && vb === null) {
    return 0;
  }
  // No-value rows sink to the bottom in either direction.
  if (va === null) {
    return 1;
  }
  if (vb === null) {
    return -1;
  }
  if (typeof va === 'string' && typeof vb === 'string') {
    return dir * va.localeCompare(vb);
  }
  return dir * (Number(va) - Number(vb));
}

export type DueBucket = 'overdue' | 'week' | 'month' | 'none';

const DUE_BUCKET_OPTIONS = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'week', label: 'Due in 7 days' },
  { value: 'month', label: 'Due in 30 days' },
  { value: 'none', label: 'No due date' },
];

function inDueBucket(iso: string | null, bucket: DueBucket, now: number): boolean {
  if (bucket === 'none') {
    return iso === null;
  }
  if (!iso) {
    return false;
  }
  const delta = dueInstant(iso) - now;
  if (bucket === 'overdue') {
    return delta < 0;
  }
  return delta >= 0 && delta <= (bucket === 'week' ? 7 : 30) * DAY_MS;
}

/** Partner-due bucket: matches when ANY assignment's np_due_date falls
 * in the bucket ('none' = no partner has a due date at all). */
function npDueMatches(c: DashboardCase, bucket: DueBucket, now: number): boolean {
  if (bucket === 'none') {
    return !c.nps.some((np) => np.npDueDate !== null);
  }
  return c.nps.some((np) => inDueBucket(np.npDueDate, bucket, now));
}

function SortableTh({
  children,
  sortKey,
  sort,
  onSort,
  nowrap,
}: Readonly<{
  children: ReactNode;
  sortKey: DashboardSortKey;
  sort: DashboardSort | null;
  onSort: (key: DashboardSortKey) => void;
  nowrap?: boolean;
}>) {
  const active = sort?.key === sortKey;
  const Icon = !active ? IconSelector : sort?.dir === 'asc' ? IconChevronUp : IconChevronDown;
  return (
    <Table.Th style={nowrap ? { whiteSpace: 'nowrap' } : undefined}>
      <UnstyledButton
        onClick={() => onSort(sortKey)}
        data-testid={`dashboard-sort-${sortKey}`}
        aria-label={`Sort by ${typeof children === 'string' ? children : sortKey}`}
        style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
      >
        <Text size="sm" fw={600} style={{ whiteSpace: 'nowrap' }}>
          {children}
        </Text>
        <Icon size={13} style={{ opacity: active ? 1 : 0.4, flex: 'none' }} />
      </UnstyledButton>
    </Table.Th>
  );
}

/** "Due in 4d" / "3d over" for the case clock. Red only past breach,
 * amber inside 2 days — the queue's colour discipline. */
function daysLeftBadge(dueAt: string | null, now = new Date()) {
  if (!dueAt) {
    return null;
  }
  const days = Math.ceil((new Date(dueAt).getTime() - now.getTime()) / DAY_MS);
  if (days < 0) {
    return { label: `${-days}d over`, color: 'red' as const };
  }
  if (days <= 2) {
    return { label: days === 0 ? 'Due today' : `${days}d left`, color: 'orange' as const };
  }
  return { label: `${days}d left`, color: 'gray' as const };
}

/** Wall-clock instants (created / completed). Due dates are a UTC
 *  calendar day and go through `formatDueDate` instead. */
function formatDate(iso: string | null): string {
  if (!iso) {
    return '—';
  }
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function caseRoute(c: DashboardCase): string | null {
  if (c.codenameFamily) {
    return `/cases/${encodeURIComponent(c.codenameFamily)}?item=rr:${c.reportRequestId}`;
  }
  return c.reportId ? `/reports/${c.reportId}` : null;
}

function matchesSearch(c: DashboardCase, needle: string, clientName: string | null): boolean {
  const haystack = [
    c.codename,
    c.configName,
    c.client?.name,
    clientName,
    c.clientReference,
    c.analyst?.name,
    c.firstQa?.name,
    c.secondQa?.name,
    c.analystNotes,
    ...c.subjects,
    ...c.nps.map((np) => np.partnerName),
  ];
  return haystack.some((h) => h?.toLowerCase().includes(needle));
}

// ---------------------------------------------------------------------------
// QA reviewer cells (avatar menus writing one report slot each)
// ---------------------------------------------------------------------------

/** Roster items live INSIDE the opened dropdown so the (module-cached)
 * roster fetch only happens when a menu actually opens — the same
 * pattern as the Queue rail's OwnerMenuItems. */
function QaMenuItems({
  slot,
  current,
  reportId,
  currentUserId,
  onAssignQa,
}: Readonly<{
  slot: QaSlot;
  current: InboxAnalyst | null;
  reportId: string;
  currentUserId: string | null;
  onAssignQa: (reportId: string, slot: QaSlot, userId: string | null) => Promise<void>;
}>) {
  const members = useAnalystRoster();
  const currentId = current?.userId ?? null;
  const label = QA_SLOT_LABELS[slot];
  return (
    <>
      <Menu.Label>{currentId ? `Reassign ${label}…` : `Assign ${label}…`}</Menu.Label>
      {currentUserId && currentId !== currentUserId && (
        <Menu.Item onClick={() => void onAssignQa(reportId, slot, currentUserId)}>
          Assign to me
        </Menu.Item>
      )}
      {members
        .filter((m) => String(m.id) !== currentId)
        .map((m) => (
          <Menu.Item key={m.id} onClick={() => void onAssignQa(reportId, slot, String(m.id))}>
            {m.name}
          </Menu.Item>
        ))}
      {currentId && (
        <>
          <Menu.Divider />
          <Menu.Item color="red" onClick={() => void onAssignQa(reportId, slot, null)}>
            Clear {label}
          </Menu.Item>
        </>
      )}
    </>
  );
}

/** One QA slot as a compact avatar menu — initials carry the assignee,
 * the tooltip the full name (the columns stay avatar-narrow). Cases
 * without a report yet have nothing to write to and render a dash. */
function QaCell({
  slot,
  caseRow,
  currentUserId,
  onAssignQa,
}: Readonly<{
  slot: QaSlot;
  caseRow: DashboardCase;
  currentUserId: string | null;
  onAssignQa: (reportId: string, slot: QaSlot, userId: string | null) => Promise<void>;
}>) {
  const current = slot === 'first' ? caseRow.firstQa : caseRow.secondQa;
  const { reportId } = caseRow;
  if (!reportId) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }
  return (
    <Menu withinPortal position="bottom-end">
      <Menu.Target>
        <UnstyledButton
          onClick={(e) => e.stopPropagation()}
          aria-label={`Assign ${QA_SLOT_LABELS[slot]}`}
          data-testid={`qa-menu-${slot}-${caseRow.reportRequestId}`}
          style={{ display: 'inline-flex', flex: 'none' }}
        >
          <AnalystAvatar analyst={current} emptyLabel="Unassigned" />
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
        <QaMenuItems
          slot={slot}
          current={current}
          reportId={reportId}
          currentUserId={currentUserId}
          onAssignQa={onAssignQa}
        />
      </Menu.Dropdown>
    </Menu>
  );
}

// ---------------------------------------------------------------------------
// Notes editor (Popover + Textarea)
// ---------------------------------------------------------------------------

function NotesCell({
  caseRow,
  onSave,
}: Readonly<{
  caseRow: DashboardCase;
  onSave: (reportRequestId: string, notes: string | null) => Promise<void>;
}>) {
  const [opened, setOpened] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const open = () => {
    setDraft(caseRow.analystNotes ?? '');
    setOpened(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await onSave(caseRow.reportRequestId, draft.trim() || null);
      setOpened(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Group gap={4} wrap="nowrap">
      {caseRow.analystNotes && (
        <Tooltip label={caseRow.analystNotes} withArrow multiline maw={360}>
          <Text size="xs" lineClamp={2} style={{ minWidth: 0 }}>
            {caseRow.analystNotes}
          </Text>
        </Tooltip>
      )}
      <Popover opened={opened} onChange={setOpened} withArrow trapFocus width={320}>
        <Popover.Target>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={open}
            aria-label="Edit analyst note"
            data-testid={`notes-edit-${caseRow.reportRequestId}`}
          >
            <IconPencil size={14} />
          </ActionIcon>
        </Popover.Target>
        <Popover.Dropdown>
          <Stack gap="xs">
            <Textarea
              value={draft}
              onChange={(e) => setDraft(e.currentTarget.value)}
              placeholder="Analyst note for this case…"
              autosize
              minRows={3}
              maxRows={8}
              data-testid="notes-textarea"
            />
            <Group justify="flex-end" gap="xs">
              <Button size="compact-xs" variant="default" onClick={() => setOpened(false)}>
                Cancel
              </Button>
              <Button
                size="compact-xs"
                loading={saving}
                onClick={() => void save()}
                data-testid="notes-save"
              >
                Save
              </Button>
            </Group>
          </Stack>
        </Popover.Dropdown>
      </Popover>
    </Group>
  );
}

// ---------------------------------------------------------------------------
// Stat tiles (label + semibold value; meaning carried by the label)
// ---------------------------------------------------------------------------

function StatTile({
  label,
  value,
  accent,
}: Readonly<{ label: string; value: number; accent?: 'red' | 'orange' }>) {
  return (
    <Stack gap={0} miw={72}>
      <Text size="lg" fw={600} c={accent && value > 0 ? accent : undefined} lh={1.2}>
        {value}
      </Text>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// The tab
// ---------------------------------------------------------------------------

export function DashboardTab({
  data,
  isLoading,
  error,
  view,
  onViewChange,
  analystFilter,
  onAnalystFilterChange,
  clientFilter,
  onClientFilterChange,
  search,
  onSearchChange,
  onSaveNotes,
  onRefresh,
  currentUserId,
  onClaim,
  qaFilter,
  onQaFilterChange,
  onAssignQa,
}: Readonly<DashboardTabProps>) {
  const navigate = useNavigate();
  const handleNavigate = useCallback((route: string) => navigate(route), [navigate]);
  const [searchDraft, setSearchDraft] = useSearchDraft(search, onSearchChange);
  const cases = useMemo(() => data?.cases ?? [], [data]);

  // Client display falls back to the team name when the team has no
  // Backoffice Organization linked — same resolution every other
  // surface (triage rail, Reports page) uses, so an on-behalf-of team
  // selected at triage always shows here too.
  const { teams } = useTeams();
  const teamNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of teams) {
      map.set(String(t.id), t.name);
    }
    return map;
  }, [teams]);
  const clientNameFor = useCallback(
    (c: DashboardCase) =>
      c.client?.name ?? (c.teamId ? (teamNameById.get(c.teamId) ?? null) : null),
    [teamNameById]
  );

  // Column sort + the due/stage/partner filters are view state, not
  // shareable slices — plain local state (unlike the URL-persisted
  // analyst/QA/search trio the page owns).
  const [sort, setSort] = useState<DashboardSort | null>(null);
  const [stageFilter, setStageFilter] = useState<string | null>(null);
  const [npStatusFilter, setNpStatusFilter] = useState<string | null>(null);
  const [dueFilter, setDueFilter] = useState<DueBucket | null>(null);
  const [npDueFilter, setNpDueFilter] = useState<DueBucket | null>(null);

  const handleSort = useCallback((key: DashboardSortKey) => {
    // asc → desc → back to the server order.
    setSort((prev) =>
      prev?.key === key ? (prev.dir === 'asc' ? { key, dir: 'desc' } : null) : { key, dir: 'asc' }
    );
  }, []);

  // Analyst filter options with per-analyst case counts — the sheet's
  // per-CX-Lead tabs collapsed into one dropdown.
  const analystOptions = useMemo(() => {
    const counts = new Map<string, { name: string; count: number }>();
    let unassigned = 0;
    for (const c of cases) {
      if (!c.analyst) {
        unassigned += 1;
        continue;
      }
      const prev = counts.get(c.analyst.userId);
      counts.set(c.analyst.userId, {
        name: c.analyst.name ?? c.analyst.userId,
        count: (prev?.count ?? 0) + 1,
      });
    }
    const options = [...counts.entries()]
      .sort((a, b) => b[1].count - a[1].count)
      .map(([userId, { name, count }]) => ({
        value: userId,
        label: `${name} (${count})`,
      }));
    if (unassigned > 0) {
      options.push({ value: UNASSIGNED, label: `Unassigned (${unassigned})` });
    }
    return options;
  }, [cases]);

  // Client filter options — one entry per client team with its case
  // count, named the way the Client column is (Organization, else the
  // team name); busiest clients first, ties alphabetical.
  const clientOptions = useMemo(() => {
    const counts = new Map<string, { name: string; count: number }>();
    for (const c of cases) {
      if (!c.teamId) {
        continue;
      }
      const prev = counts.get(c.teamId);
      counts.set(c.teamId, {
        name: prev?.name ?? clientNameFor(c) ?? c.teamId,
        count: (prev?.count ?? 0) + 1,
      });
    }
    return [...counts.entries()]
      .sort((a, b) => b[1].count - a[1].count || a[1].name.localeCompare(b[1].name))
      .map(([teamId, { name, count }]) => ({ value: teamId, label: `${name} (${count})` }));
  }, [cases, clientNameFor]);

  // QA filter options — a person occupying EITHER slot matches, so
  // counts are per-case (someone holding both slots counts once).
  const qaOptions = useMemo(() => {
    const counts = new Map<string, { name: string; count: number }>();
    let unassigned = 0;
    for (const c of cases) {
      const slots = [c.firstQa, c.secondQa].filter((qa): qa is InboxAnalyst => qa !== null);
      if (slots.length === 0) {
        unassigned += 1;
        continue;
      }
      for (const qa of new Map(slots.map((s) => [s.userId, s])).values()) {
        const prev = counts.get(qa.userId);
        counts.set(qa.userId, {
          name: qa.name ?? qa.userId,
          count: (prev?.count ?? 0) + 1,
        });
      }
    }
    const options = [...counts.entries()]
      .sort((a, b) => b[1].count - a[1].count)
      .map(([userId, { name, count }]) => ({
        value: userId,
        label: `${name} (${count})`,
      }));
    if (unassigned > 0) {
      options.push({ value: UNASSIGNED, label: `No QA (${unassigned})` });
    }
    return options;
  }, [cases]);

  // Stage / partner-status options with per-value counts, mirroring the
  // analyst dropdown (counts over the unfiltered view).
  const stageOptions = useMemo(() => {
    const counts = new Map<string, { label: string; count: number }>();
    for (const c of cases) {
      const prev = counts.get(c.stage);
      counts.set(c.stage, { label: stageChip(c).label, count: (prev?.count ?? 0) + 1 });
    }
    return [...counts.entries()]
      .sort((a, b) => (STAGE_ORDER[a[0]] ?? 999) - (STAGE_ORDER[b[0]] ?? 999))
      .map(([value, { label, count }]) => ({ value, label: `${label} (${count})` }));
  }, [cases]);

  const npStatusOptions = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of cases) {
      for (const status of new Set(c.nps.map((np) => np.status))) {
        counts.set(status, (counts.get(status) ?? 0) + 1);
      }
    }
    return [...counts.entries()]
      .sort((a, b) => (NP_STATUS_ORDER[a[0]] ?? 999) - (NP_STATUS_ORDER[b[0]] ?? 999))
      .map(([value, count]) => ({ value, label: `${npStatusChip(value).label} (${count})` }));
  }, [cases]);

  const filtered = useMemo(() => {
    const needle = searchDraft.trim().toLowerCase();
    const now = Date.now();
    return cases.filter((c) => {
      if (analystFilter === UNASSIGNED && c.analyst !== null) {
        return false;
      }
      if (analystFilter && analystFilter !== UNASSIGNED && c.analyst?.userId !== analystFilter) {
        return false;
      }
      if (clientFilter && c.teamId !== clientFilter) {
        return false;
      }
      if (qaFilter === UNASSIGNED && (c.firstQa !== null || c.secondQa !== null)) {
        return false;
      }
      if (
        qaFilter &&
        qaFilter !== UNASSIGNED &&
        c.firstQa?.userId !== qaFilter &&
        c.secondQa?.userId !== qaFilter
      ) {
        return false;
      }
      if (stageFilter && c.stage !== stageFilter) {
        return false;
      }
      if (npStatusFilter && !c.nps.some((np) => np.status === npStatusFilter)) {
        return false;
      }
      if (dueFilter && !inDueBucket(c.dueAt, dueFilter, now)) {
        return false;
      }
      if (npDueFilter && !npDueMatches(c, npDueFilter, now)) {
        return false;
      }
      return needle ? matchesSearch(c, needle, clientNameFor(c)) : true;
    });
  }, [
    cases,
    analystFilter,
    clientFilter,
    qaFilter,
    stageFilter,
    npStatusFilter,
    dueFilter,
    npDueFilter,
    searchDraft,
    clientNameFor,
  ]);

  const sorted = useMemo(() => {
    // Ignore a sort keyed to a column the current view doesn't render.
    const hidden: DashboardSortKey[] =
      view === 'completed' ? ['stage', 'due'] : ['completed', 'delivery'];
    if (!sort || hidden.includes(sort.key)) {
      return filtered;
    }
    return [...filtered].sort((a, b) => compareCases(a, b, sort, clientNameFor));
  }, [filtered, sort, view, clientNameFor]);

  const stats = useMemo(() => {
    const now = Date.now();
    let overdue = 0;
    let dueSoon = 0;
    let unassigned = 0;
    for (const c of cases) {
      if (!c.analyst) {
        unassigned += 1;
      }
      if (c.dueAt) {
        const remaining = new Date(c.dueAt).getTime() - now;
        if (remaining < 0) {
          overdue += 1;
        } else if (remaining <= 7 * DAY_MS) {
          dueSoon += 1;
        }
      }
    }
    return { total: cases.length, overdue, dueSoon, unassigned };
  }, [cases]);

  return (
    <Stack gap="md" data-testid="dashboard-tab">
      <Group justify="space-between" wrap="wrap" gap="sm">
        <Group gap="sm" wrap="wrap">
          <SegmentedControl
            size="xs"
            value={view}
            onChange={(v) => onViewChange(v as DashboardView)}
            data={[
              { value: 'active', label: 'Active' },
              { value: 'completed', label: 'Completed this year' },
            ]}
            data-testid="dashboard-view"
          />
          <Select
            size="xs"
            placeholder="All analysts"
            data={analystOptions}
            value={analystFilter}
            onChange={onAnalystFilterChange}
            clearable
            searchable
            w={190}
            data-testid="dashboard-analyst-filter"
          />
          <Select
            size="xs"
            placeholder="All clients"
            data={clientOptions}
            value={clientFilter}
            onChange={onClientFilterChange}
            clearable
            searchable
            w={200}
            data-testid="dashboard-client-filter"
          />
          <Select
            size="xs"
            placeholder="All QAers"
            data={qaOptions}
            value={qaFilter}
            onChange={onQaFilterChange}
            clearable
            searchable
            w={170}
            data-testid="dashboard-qa-filter"
          />
          {view === 'active' && (
            <>
              <Select
                size="xs"
                placeholder="All stages"
                data={stageOptions}
                value={stageFilter}
                onChange={setStageFilter}
                clearable
                w={170}
                data-testid="dashboard-stage-filter"
              />
              <Select
                size="xs"
                placeholder="Partner status"
                data={npStatusOptions}
                value={npStatusFilter}
                onChange={setNpStatusFilter}
                clearable
                w={160}
                data-testid="dashboard-np-status-filter"
              />
              <Select
                size="xs"
                placeholder="Client due"
                data={DUE_BUCKET_OPTIONS}
                value={dueFilter}
                onChange={(v) => setDueFilter(v as DueBucket | null)}
                clearable
                w={150}
                data-testid="dashboard-due-filter"
              />
              <Select
                size="xs"
                placeholder="Partner due"
                data={DUE_BUCKET_OPTIONS}
                value={npDueFilter}
                onChange={(v) => setNpDueFilter(v as DueBucket | null)}
                clearable
                w={150}
                data-testid="dashboard-np-due-filter"
              />
            </>
          )}
          <TextInput
            size="xs"
            placeholder="Search cases, subjects, clients, references, partners…"
            leftSection={<IconSearch size={14} />}
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.currentTarget.value)}
            w={280}
            data-testid="dashboard-search"
          />
        </Group>
        <Group gap="lg" wrap="nowrap" data-testid="dashboard-stats">
          <StatTile label={view === 'active' ? 'Active cases' : 'Completed'} value={stats.total} />
          {view === 'active' && (
            <>
              <StatTile label="Overdue" value={stats.overdue} accent="red" />
              <StatTile label="Due in 7 days" value={stats.dueSoon} accent="orange" />
              <StatTile label="Unassigned" value={stats.unassigned} />
            </>
          )}
          <Group gap={6} wrap="nowrap">
            <Tooltip
              label={data ? `Generated ${timeAgo(data.generatedAt)}` : 'Not loaded yet'}
              withArrow
            >
              <ActionIcon
                variant="subtle"
                color="gray"
                onClick={() => void onRefresh()}
                aria-label="Refresh dashboard"
                data-testid="dashboard-refresh"
              >
                <IconRefresh size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Group>

      {error && (
        <Text c="red" size="sm">
          Failed to load the dashboard: {error.message}
        </Text>
      )}

      {isLoading && cases.length === 0 ? (
        <Center mih={200}>
          <Loader size="md" />
        </Center>
      ) : filtered.length === 0 ? (
        <Center mih={200}>
          <Text c="dimmed">
            {cases.length === 0 ? 'No cases in this view.' : 'No cases match the filters.'}
          </Text>
        </Center>
      ) : (
        <CasesGrid
          cases={sorted}
          view={view}
          onNavigate={handleNavigate}
          onSaveNotes={onSaveNotes}
          currentUserId={currentUserId}
          onClaim={onClaim}
          onAssignQa={onAssignQa}
          sort={sort}
          onSort={handleSort}
          clientNameFor={clientNameFor}
        />
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// The table
// ---------------------------------------------------------------------------

function NpCells({ np }: Readonly<{ np: DashboardNp | null }>) {
  if (!np) {
    return (
      <>
        <Table.Td>
          <Text size="sm" c="dimmed">
            —
          </Text>
        </Table.Td>
        <Table.Td />
        <Table.Td />
      </>
    );
  }
  const chip = npStatusChip(np.status);
  const name =
    np.partnerName ??
    (np.status === 'shortlisting' && np.shortlistCount > 0
      ? `${np.shortlistCount} shortlisted`
      : '—');
  return (
    <>
      <Table.Td>
        <Text size="sm" c={np.partnerName ? undefined : 'dimmed'}>
          {name}
        </Text>
      </Table.Td>
      <Table.Td style={{ whiteSpace: 'nowrap' }}>
        <Tooltip label={chip.label} withArrow>
          <Badge
            size="sm"
            variant="light"
            color={chip.color}
            style={{ textTransform: 'none' }}
            leftSection={np.submitted ? <IconCheck size={11} /> : undefined}
          >
            {chip.label}
          </Badge>
        </Tooltip>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          {np.npDueDate ? formatDueDate(np.npDueDate) : '—'}
        </Text>
      </Table.Td>
    </>
  );
}

function CaseCells({
  caseRow,
  view,
  onNavigate,
  currentUserId,
  onClaim,
  onAssignQa,
  clientName,
}: Readonly<{
  caseRow: DashboardCase;
  view: DashboardView;
  onNavigate: (route: string) => void;
  currentUserId: string | null;
  onClaim: (familyKey: string, userId: string | null) => Promise<void>;
  onAssignQa: (reportId: string, slot: QaSlot, userId: string | null) => Promise<void>;
  /** Resolved display name: linked Organization, else the team name. */
  clientName: string | null;
}>) {
  const chip = stageChip(caseRow);
  const due = view === 'active' ? daysLeftBadge(caseRow.dueAt) : null;
  const route = caseRoute(caseRow);
  return (
    <>
      <Table.Td>
        <Group gap={6} wrap="nowrap">
          {caseRow.codenameFamily ? (
            // Same claim/assign dropdown as the Queue rail — claims are
            // family-wide, so it needs a family key; family-less legacy
            // rows keep the inert avatar.
            <OwnerAvatarMenu
              analyst={caseRow.analyst}
              owner={{
                familyKey: caseRow.codenameFamily,
                caseAnalyst: caseRow.analyst,
                currentUserId,
                onClaim,
              }}
            />
          ) : (
            <AnalystAvatar analyst={caseRow.analyst} />
          )}
          <Text size="sm" c={caseRow.analyst ? undefined : 'dimmed'} truncate="end" maw={140}>
            {caseRow.analyst ? (caseRow.analyst.name ?? caseRow.analyst.userId) : 'Unassigned'}
          </Text>
        </Group>
      </Table.Td>
      <Table.Td>
        <QaCell
          slot="first"
          caseRow={caseRow}
          currentUserId={currentUserId}
          onAssignQa={onAssignQa}
        />
      </Table.Td>
      <Table.Td>
        <QaCell
          slot="second"
          caseRow={caseRow}
          currentUserId={currentUserId}
          onAssignQa={onAssignQa}
        />
      </Table.Td>
      <Table.Td>
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Group gap={6} wrap="nowrap">
            {route ? (
              <UnstyledButton
                onClick={() => onNavigate(route)}
                data-testid={`dashboard-case-link-${caseRow.reportRequestId}`}
              >
                <Text size="sm" fw={600} c="blue" style={{ whiteSpace: 'nowrap' }}>
                  {caseRow.codename ?? '(uncoded)'}
                </Text>
              </UnstyledButton>
            ) : (
              <Text size="sm" fw={600} style={{ whiteSpace: 'nowrap' }}>
                {caseRow.codename ?? '(uncoded)'}
              </Text>
            )}
            {caseRow.expedited && (
              <Badge size="xs" variant="light" color="orange" style={{ flex: 'none' }}>
                Expedited
              </Badge>
            )}
          </Group>
          {(caseRow.subjects.length > 0 || caseRow.configName) && (
            <Text size="xs" c="dimmed" lineClamp={1}>
              {[caseRow.subjects.join(', '), caseRow.configName].filter(Boolean).join(' · ')}
            </Text>
          )}
        </Stack>
      </Table.Td>
      <Table.Td>
        <Text
          size="sm"
          c="dimmed"
          truncate="end"
          maw={150}
          title={clientName ?? undefined}
          data-testid={`dashboard-client-${caseRow.reportRequestId}`}
        >
          {clientName ?? '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text
          size="sm"
          c="dimmed"
          truncate="end"
          maw={130}
          title={caseRow.clientReference ?? undefined}
          data-testid={`dashboard-ref-${caseRow.reportRequestId}`}
        >
          {caseRow.clientReference ?? '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
          {formatDate(caseRow.createdAt)}
        </Text>
      </Table.Td>
      {view === 'completed' && (
        <>
          <Table.Td>
            <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
              {formatDate(caseRow.completedAt)}
            </Text>
          </Table.Td>
          <Table.Td>
            {caseRow.onTime === null ? (
              <Text size="sm" c="dimmed">
                —
              </Text>
            ) : (
              <Badge
                size="sm"
                variant="light"
                color={caseRow.onTime ? 'green' : 'red'}
                style={{ textTransform: 'none' }}
              >
                {caseRow.onTime ? 'On time' : 'Late'}
              </Badge>
            )}
          </Table.Td>
        </>
      )}
      {view === 'active' && (
        <>
          {/* nowrap keeps the table from squeezing the pill into an
              ellipsis; the tooltip is the backstop for tight layouts. */}
          <Table.Td style={{ whiteSpace: 'nowrap' }}>
            <Tooltip label={chip.label} withArrow>
              <Badge size="sm" variant="light" color={chip.color} style={{ textTransform: 'none' }}>
                {chip.label}
              </Badge>
            </Tooltip>
          </Table.Td>
          <Table.Td>
            <Group gap={6} wrap="nowrap">
              <Text size="sm" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                {caseRow.dueAt
                  ? formatDueDate(caseRow.dueAt)
                  : caseRow.turnaroundDays != null
                    ? `TAT ${caseRow.turnaroundDays}d`
                    : '—'}
              </Text>
              {due && (
                <Badge
                  size="sm"
                  variant="light"
                  color={due.color}
                  style={{ textTransform: 'none', flex: 'none' }}
                  data-testid={`due-badge-${caseRow.reportRequestId}`}
                >
                  {due.label}
                </Badge>
              )}
            </Group>
          </Table.Td>
        </>
      )}
    </>
  );
}

/**
 * One case = its case row plus one continuation row per extra NP.
 * Memoized so a keystroke in the (client-side) search only re-renders
 * the rows whose case object actually changed — the filtered array is
 * new each time, but the row objects inside keep their identity.
 */
const CaseRowGroup = memo(
  ({
    caseRow,
    view,
    onNavigate,
    onSaveNotes,
    currentUserId,
    onClaim,
    onAssignQa,
    clientName,
  }: Readonly<{
    caseRow: DashboardCase;
    view: DashboardView;
    onNavigate: (route: string) => void;
    onSaveNotes: (reportRequestId: string, notes: string | null) => Promise<void>;
    currentUserId: string | null;
    onClaim: (familyKey: string, userId: string | null) => Promise<void>;
    onAssignQa: (reportId: string, slot: QaSlot, userId: string | null) => Promise<void>;
    clientName: string | null;
  }>) => {
    const [firstNp, ...restNps] = caseRow.nps;
    return (
      <>
        <Table.Tr data-testid={`dashboard-case-${caseRow.reportRequestId}`}>
          <CaseCells
            caseRow={caseRow}
            view={view}
            onNavigate={onNavigate}
            currentUserId={currentUserId}
            onClaim={onClaim}
            onAssignQa={onAssignQa}
            clientName={clientName}
          />
          <NpCells np={firstNp ?? null} />
          <Table.Td>
            <NotesCell caseRow={caseRow} onSave={onSaveNotes} />
          </Table.Td>
        </Table.Tr>
        {restNps.map((np) => (
          <Table.Tr key={np.assignmentId} data-testid={`dashboard-np-${np.assignmentId}`}>
            {PAD_KEYS.map((k) => (
              <Table.Td key={k} />
            ))}
            <NpCells np={np} />
            <Table.Td />
          </Table.Tr>
        ))}
      </>
    );
  }
);

function CasesGrid({
  cases,
  view,
  onNavigate,
  onSaveNotes,
  currentUserId,
  onClaim,
  onAssignQa,
  sort,
  onSort,
  clientNameFor,
}: Readonly<{
  cases: DashboardCase[];
  view: DashboardView;
  onNavigate: (route: string) => void;
  onSaveNotes: (reportRequestId: string, notes: string | null) => Promise<void>;
  currentUserId: string | null;
  onClaim: (familyKey: string, userId: string | null) => Promise<void>;
  onAssignQa: (reportId: string, slot: QaSlot, userId: string | null) => Promise<void>;
  sort: DashboardSort | null;
  onSort: (key: DashboardSortKey) => void;
  clientNameFor: (c: DashboardCase) => string | null;
}>) {
  const th = { sort, onSort };
  return (
    <Table.ScrollContainer minWidth={1420}>
      <Table striped highlightOnHover verticalSpacing={6}>
        <Table.Thead>
          <Table.Tr>
            <SortableTh sortKey="analyst" {...th}>
              Analyst
            </SortableTh>
            <SortableTh sortKey="qa1" {...th} nowrap>
              1st QA
            </SortableTh>
            <SortableTh sortKey="qa2" {...th} nowrap>
              2nd QA
            </SortableTh>
            <SortableTh sortKey="case" {...th}>
              Case
            </SortableTh>
            <SortableTh sortKey="client" {...th}>
              Client
            </SortableTh>
            <SortableTh sortKey="ref" {...th} nowrap>
              Client ref
            </SortableTh>
            <SortableTh sortKey="opened" {...th}>
              Opened
            </SortableTh>
            {view === 'completed' ? (
              <>
                <SortableTh sortKey="completed" {...th}>
                  Completed
                </SortableTh>
                <SortableTh sortKey="delivery" {...th}>
                  Delivery
                </SortableTh>
              </>
            ) : (
              <>
                <SortableTh sortKey="stage" {...th}>
                  Stage
                </SortableTh>
                <SortableTh sortKey="due" {...th} nowrap>
                  Client due
                </SortableTh>
              </>
            )}
            <SortableTh sortKey="partner" {...th}>
              Partner
            </SortableTh>
            <SortableTh sortKey="partnerStatus" {...th} nowrap>
              Partner status
            </SortableTh>
            <SortableTh sortKey="partnerDue" {...th} nowrap>
              Partner due
            </SortableTh>
            <SortableTh sortKey="notes" {...th}>
              Notes
            </SortableTh>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {cases.map((c) => (
            <CaseRowGroup
              key={c.reportRequestId}
              caseRow={c}
              view={view}
              onNavigate={onNavigate}
              onSaveNotes={onSaveNotes}
              currentUserId={currentUserId}
              onClaim={onClaim}
              onAssignQa={onAssignQa}
              clientName={clientNameFor(c)}
            />
          ))}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}
